/**
 * @description
 * - Maintains a single server object at a time.
 * - Any operation specific to one server that needs to broadcast an update after it resolves should be called from here.
 * - Provides an onChange method
 */
;(function () {
  'use strict'

  angular
    .module('contrast.current-server')
    .factory('currentServerService', currentServerServiceFactory)

  function currentServerServiceFactory(
    $q,
    $rootScope,
    CURRENT_SERVER_EVENTS,
    ServerService,
    ServerPropertiesService,
    AGENT_LANGUAGE,
    ORGANIZATION_SWITCH_EVENT
  ) {
    'ngInject'

    var _loaded,
      _model = {},
      deregisterOrganizationSwitchedListener

    var service = {
      init: init,
      get: get,
      toggleDefend: toggleDefend,
      toggleAssess: toggleAssess,
      onChange: onChange,
      launchApplyProtectionDialog: launchApplyProtectionDialog
    }

    return service

    function _getServerId() {
      return _model.serverId
    }

    /**
     * @description
     * - Call this from the code point (probably a controller) that will need the server data first.
     * - Any other controllers can just listen with onChange.
     * @param serverId
     * @param {{}} [options] - options object passed to this service's `get` method
     * @returns {*}
     */
    function init(serverId, options) {
      _loaded = false
      _model.serverId = serverId
      _initOrganizationSwitchedListener()
      return get(false, options)
    }

    /**
     * @description Returns the current server object
     * @param {boolean} [breakCache]
     * @param {{}} [options]
     * @param {boolean} options.buildDotNet - builds extra .NET properties on the server object
     * @returns {*}
     */
    function get(breakCache, options) {
      if (!angular.isObject(options)) {
        options = {
          buildDotNet: false
        }
      }
      if (breakCache || !_loaded) {
        return ServerService.get(_getServerId())
          .then(function (server) {
            _model.server = server
            if (
              options.buildDotNet &&
              (_.includes(AGENT_LANGUAGE.DOTNET, server.language) ||
                _.includes(AGENT_LANGUAGE.DOTNET_CORE, server.language))
            ) {
              return _buildDotNet()
                .then(_buildOsInfo)
                .then(_buildDisplayStrings)
            }
          })
          .then(function () {
            _loaded = true
            return _model.server
          })
      } else {
        return $q.when(_model.server)
      }
    }

    // Load and add .NET server properties data to server object
    function _buildDotNet() {
      var server = _model.server
      return ServerPropertiesService.buildDotNetServer(_getServerId()).then(
        function (data) {
          server.serverProperties = data
          var i = 0,
            len = server.applications.length,
            app
          for (; i < len; i++) {
            app = server.applications[i]
            app.appPoolName = _getAppPoolByAppName(app.name)
          }
        }
      )
    }

    function _buildOsInfo() {
      var server = _model.server
      if (server && server.serverProperties) {
        var os = server.serverProperties.os_name
        if (os) {
          server.os = os
        } else {
          server.os = null
          return
        }

        var bitness = server.serverProperties.cpu_architecture
        if (bitness) {
          server.os += ' '
          server.os += bitness
        }

        if (server.serverProperties.custom) {
          var version = server.serverProperties.custom['os.version']
          if (version) {
            server.os += ' / '
            server.os += version
          }

          var servicePack = server.serverProperties.custom['os.servicepack']
          if (servicePack) {
            server.os += ' / '
            server.os += servicePack
          }
        }
      }
    }

    function _buildDisplayStrings() {
      var server = _model.server
      if (server.serverProperties) {
        ServerPropertiesService.buildFormattedProperties(_model.server)
      }
    }

    function _getAppPoolByAppName(appName) {
      var serverProperties = _model.server.serverProperties
      if (
        serverProperties &&
        serverProperties.custom &&
        serverProperties.custom.appPools
      ) {
        var appPool = _.find(serverProperties.custom.appPools, function (item) {
          return _.includes(item.applicationNames, appName)
        })
        if (appPool) {
          return appPool.name
        }
      }
      return null
    }

    /**
     * Functions that will broadcast an update for all onChange listeners
     */

    function toggleDefend() {
      return ServerService.toggleDefend(_getServerId()).then(_broadcast)
    }

    function toggleAssess() {
      return ServerService.toggleAssess(_getServerId()).then(_broadcast)
    }

    function launchApplyProtectionDialog() {
      return ServerService.launchApplyProtectionDialog(_model.server).then(
        _broadcast
      )
    }

    /**
     * Event related functions
     */

    /**
     * @param {function} cb
     * @param {{}} options
     * @returns {function} deregister event function
     */
    function onChange(cb, options) {
      // TODO: CONTRAST-18296
      // eslint-disable-next-line angular/on-watch
      return $rootScope.$on(CURRENT_SERVER_EVENTS.SERVER_CHANGE, function () {
        get(true, options).then(cb)
      })
    }

    function _broadcast() {
      $rootScope.$broadcast(CURRENT_SERVER_EVENTS.SERVER_CHANGE, _model.server)
    }

    function _initOrganizationSwitchedListener() {
      if (angular.isFunction(deregisterOrganizationSwitchedListener)) {
        deregisterOrganizationSwitchedListener()
      }

      deregisterOrganizationSwitchedListener = $rootScope.$on(
        ORGANIZATION_SWITCH_EVENT,
        function () {
          _model = {}
        }
      )
    }
  }
})()
